import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from "@nextui-org/react";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import useSWR from "swr";
import { getFetcher, postFetcher } from "../../../../../services/api";
import useListCompany from "../../../../../hooks/useListCompany";
import useAuth from "../../../../../hooks/useAuth";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import ControlledInput from "../../../../elements/ControlledInput";
import { reportAddMaterialPurchaseValidation } from "../../../../../services/validation/report-purchase.validation";
import { cariBahanB3DariCas, isResponseErrorObject } from "../../../../../services/helpers";
import StatusPermohonanRegistrasi from "../../../../../enums/statusRegistrasi";

export default function ModalAddMaterialPurchase({ mutate, setSelectedCompany, setSelectedMaterial, setSelectedPeriod }) {
  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({ resolver: yupResolver(reportAddMaterialPurchaseValidation) });
  const tipePembelian = watch("tipePembelian");
  const companyId = watch("companyId");
  const registrasiId = watch("registrasiId");
  const supplierCompanyId = watch("supplierCompanyId");
  const supplierRegistrasiId = watch("supplierRegistrasiId");
  const { user, isSuperAdmin } = useAuth();

  // Perusahaan pengguna yang login. Untuk pembelian IMPOR, pelapor adalah
  // perusahaan itu sendiri - pengguna perusahaan tidak boleh memilih yang lain.
  // Super admin dikecualikan karena ia memang perlu mengisikan untuk perusahaan
  // mana pun.
  const perusahaanSendiri = user?.companies?.[0]?.company;
  const kunciPerusahaanSendiri = !isSuperAdmin && !!perusahaanSendiri?.id;
  const periodId = watch("periodId");

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const { data: dataMaterial, isLoading: isLoadingMaterial } = useSWR(`/api/data-master/bahan-b3?returnAll=true`, getFetcher);
  const { data: dataPeriods, isLoading: isLoadingPeriods } = useSWR(`/api/period/actives`, getFetcher);
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data: dataRegistration, isLoading: isLoadingRegistration } = useSWR(
    companyId ? `/api/registrasi/find-all/search?returnAll=true&status=${StatusPermohonanRegistrasi.SELESAI}&companyId=${companyId}&type_registrasi=import,impor` : null,
    getFetcher
  );

  /*
   * Pembelian LOKAL dibeli DARI perusahaan lain, jadi daftarnya bukan
   * perusahaan pelapor melainkan calon pemasok: bertipe Impor atau Produksi B3
   * dan sudah tertaut SIMPEL. Disaring di backend supaya seluruh 8.585
   * perusahaan tidak perlu diunduh.
   */
  const { dataCompany: dataPemasok, isLoadingCompany: isLoadingPemasok } = useListCompany(
    tipePembelian === "Lokal" ? { tipePerusahaan: ["Impor B3", "Produksi B3"], hasIdCompanySimpel: true, semuaPerusahaan: true } : {},
  );

  // SK milik pemasok terpilih. Tanpa saringan tipe: yang dibeli lokal bisa
  // berasal dari SK impor maupun produksi milik pemasok.
  const { data: dataSKPemasok, isLoading: isLoadingSKPemasok } = useSWR(
    supplierCompanyId ? `/api/registrasi/find-all/search?returnAll=true&status=${StatusPermohonanRegistrasi.SELESAI}&companyId=${supplierCompanyId}` : null,
    getFetcher,
  );

  /*
   * Pelapor selalu perusahaan sendiri, baik pembelian IMPOR maupun LOKAL -
   * pada pembelian lokal pun yang melapor adalah pembelinya, sedangkan penjual
   * diisi terpisah di kolom Perusahaan Pemasok. Diisikan otomatis dan kolomnya
   * dikunci untuk pengguna perusahaan; super admin dibiarkan memilih sendiri
   * karena akunnya memang tidak tertaut perusahaan.
   */
  useEffect(() => {
    if (kunciPerusahaanSendiri && companyId !== perusahaanSendiri.id) {
      setValue("companyId", perusahaanSendiri.id);
    }
  }, [kunciPerusahaanSendiri, perusahaanSendiri?.id, companyId, setValue]);

  // Berganti pemasok membatalkan SK yang sudah terpilih - SK itu milik pemasok
  // sebelumnya dan tidak lagi sah.
  useEffect(() => {
    setValue("supplierRegistrasiId", "");
    setValue("no_surat_registrasi", "");
  }, [supplierCompanyId, setValue]);

  // Nomor SK pemasok tetap disimpan sebagai teks, supaya tampilan lama yang
  // membacanya tidak ikut berubah.
  useEffect(() => {
    if (!supplierRegistrasiId) return;
    const sk = dataSKPemasok?.registrasi?.find((item) => item.id === supplierRegistrasiId);
    if (sk) setValue("no_surat_registrasi", sk.nomor_surat_formatted ?? sk.nomor_surat ?? "");
  }, [supplierRegistrasiId, dataSKPemasok, setValue]);

  // Set default period when periods are loaded
  useEffect(() => {
    if (dataPeriods?.data && dataPeriods.data.length > 0 && !periodId) {
      setValue("periodId", dataPeriods.data[0].id);
    }
  }, [dataPeriods, periodId, setValue]);

  /*
   * Label SK diberi pembeda berupa isi bahan B3-nya.
   *
   * Nomor surat saja tidak cukup: ada dokumen SK BERBEDA yang nomornya tercetak
   * sama - mis. dua S.1693/PB3/PB3/PLB.1/10/2016 milik Firmenich Indonesia,
   * yang tanggal suratnya pun sama - sehingga dua baris tampil identik dan
   * pengguna memilih tanpa bisa membedakan. Isi bahan B3-nya yang membedakan.
   */
  const labelSK = (sk) => {
    const nomor = sk?.nomor_surat_formatted ?? sk?.nomor_surat ?? "(tanpa nomor)";
    const bahan = [...new Set((sk?.BahanB3Registrasi ?? []).map((item) => item?.nama_bahan?.trim()).filter(Boolean))];

    if (bahan.length === 0) return nomor;

    const ditampilkan = bahan.slice(0, 3).join(", ");
    return `${nomor} - ${ditampilkan}${bahan.length > 3 ? ` +${bahan.length - 3} lainnya` : ""}`;
  };

  /*
   * SK yang sedang dipilih - milik sendiri untuk pembelian IMPOR, milik pemasok
   * untuk pembelian LOKAL.
   */
  const skTerpilih = useMemo(() => {
    if (tipePembelian === "Import") return dataRegistration?.registrasi?.find((item) => item.id === registrasiId) ?? null;
    if (tipePembelian === "Lokal") return dataSKPemasok?.registrasi?.find((item) => item.id === supplierRegistrasiId) ?? null;
    return null;
  }, [tipePembelian, dataRegistration, registrasiId, dataSKPemasok, supplierRegistrasiId]);

  /*
   * Pilihan bahan B3 berasal dari ISI SK, bukan dari seluruh data master.
   *
   * Sebelumnya daftar untuk pembelian lokal memuat seluruh master - bahan yang
   * tidak tercantum pada SK pun bisa dipilih - sedangkan untuk impor kolomnya
   * dikunci dan diisi otomatis dari BahanB3Registrasi[0] saja, sehingga SK yang
   * memuat lebih dari satu bahan hanya bisa dilaporkan bahan pertamanya.
   *
   * Nomor CAS pada SK dicocokkan ke master dengan aturan yang sama dengan
   * backend (lihat cariBahanB3DariCas), supaya yang bisa dipilih di sini persis
   * yang diterima saat disimpan.
   */
  const opsiBahanB3 = useMemo(() => {
    const master = dataMaterial?.data ?? [];
    const bahanSK = skTerpilih?.BahanB3Registrasi ?? [];
    const hasil = [];
    const sudahAda = new Set();

    for (const bahan of bahanSK) {
      const cocok = cariBahanB3DariCas(master, bahan?.cas_number);
      if (!cocok || sudahAda.has(cocok.id)) continue;
      sudahAda.add(cocok.id);
      hasil.push({ value: cocok.id, label: `${cocok.casNumber} - ${cocok.namaBahanKimia}` });
    }

    return hasil;
  }, [dataMaterial, skTerpilih]);

  /*
   * SK berganti: satu bahan langsung dipilihkan, lebih dari satu dibiarkan
   * dipilih sendiri, dan pilihan lama selalu dibuang supaya tidak tertinggal
   * bahan milik SK sebelumnya.
   */
  useEffect(() => {
    if (!skTerpilih) return;
    if (!dataMaterial) return;

    if (opsiBahanB3.length === 1) {
      setValue("dataBahanB3Id", opsiBahanB3[0].value);
      return;
    }

    setValue("dataBahanB3Id", "");

    if (opsiBahanB3.length === 0) {
      toast.error("Tidak ada data bahan B3 dalam SK yang dikenali di master. Segera update di menu Daftar Registrasi");
    }
  }, [skTerpilih, opsiBahanB3, dataMaterial, setValue]);

  function handleOnClose() {
    reset({
      // Perusahaan yang terkunci tidak dikosongkan saat modal ditutup - efek di
      // atas hanya akan mengisinya lagi, dan sempat kosong berarti daftar SK
      // ikut kosong sesaat.
      companyId: kunciPerusahaanSendiri ? perusahaanSendiri.id : "",
      periodId: "",
      dataBahanB3Id: "",
      tipePembelian: "",
      registrasiId: "",
      no_surat_registrasi: "",
      supplierCompanyId: "",
      supplierRegistrasiId: "",
    });
    onClose();
  }

  async function handleOnSubmit(data) {
    try {
      await postFetcher("/api/pelaporan-pembelian/generate", data);
      mutate();
      const company = dataCompany?.data?.find((item) => item.id === data.companyId);
      const period = dataPeriods?.data?.find((item) => item.id === data.periodId);

      setSelectedCompany({ value: company.id, label: company.name });
      setSelectedPeriod({ value: period.id, label: period.name });
      setSelectedMaterial(data.dataBahanB3Id);
      toast.success("Laporan bahan b3 berhasil ditambah!");
      handleOnClose();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  return (
    <>
      <Button size="sm" onPress={onOpen} color="primary" startContent={<PlusIcon className="size-4" />}>
        Tambah Bahan B3
      </Button>
      <Modal size="lg" isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleOnClose} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <ModalHeader className="flex flex-col gap-1">Buat Laporan Pembelian Bahan B3</ModalHeader>
              <ModalBody className="flex flex-col gap-3">
                <Select
                  {...register("periodId")}
                  variant="faded"
                  isRequired
                  disallowEmptySelection
                  label="Periode"
                  labelPlacement="outside"
                  placeholder="Pilih Periode..."
                  color={errors.periodId ? "danger" : "default"}
                  isInvalid={errors.periodId}
                  errorMessage={errors.periodId && errors.periodId.message}
                  isLoading={isLoadingPeriods}
                >
                  {dataPeriods?.data?.map((period) => (
                    <SelectItem key={period.id} value={period.id}>
                      {period.name}
                    </SelectItem>
                  ))}
                </Select>

                <Select
                  {...register("tipePembelian")}
                  variant="faded"
                  isRequired
                  disallowEmptySelection
                  label="Tipe Pembelian"
                  labelPlacement="outside"
                  placeholder="Pilih..."
                  color={errors.tipePembelian ? "danger" : "default"}
                  isInvalid={errors.tipePembelian}
                  errorMessage={errors.tipePembelian && errors.tipePembelian.message}
                >
                  <SelectItem key="Import">Import</SelectItem>
                  <SelectItem key="Lokal">Lokal</SelectItem>
                </Select>

                {/* Pelapor: selalu perusahaan sendiri, untuk impor maupun lokal.
                    Super admin tetap bebas memilih. */}
                <ControlledReactSelect
                  control={control}
                  name="companyId"
                  label="Perusahaan"
                  options={dataCompany?.data?.map((item) => ({ value: item.id, label: item.name }))}
                  isLoading={isLoadingCompany}
                  isDisabled={kunciPerusahaanSendiri}
                />

                {tipePembelian === "Import" && (
                  <ControlledReactSelect
                    control={control}
                    name="registrasiId"
                    label="Surat Registrasi Bahan B3"
                    options={dataRegistration?.registrasi?.map((item) => ({ value: item.id, label: labelSK(item) }))}
                    isLoading={isLoadingRegistration}
                  />
                )}

                {/* LOKAL: dibeli DARI perusahaan lain. Pemasok dan SK-nya dipilih
                    dari daftar, bukan diketik bebas. Pemilik laporan tetap kolom
                    Perusahaan di atas. */}
                {tipePembelian === "Lokal" && (
                  <>
                    <ControlledReactSelect
                      control={control}
                      name="supplierCompanyId"
                      label="Perusahaan Pemasok"
                      options={dataPemasok?.data?.map((item) => ({ value: item.id, label: item.name }))}
                      isLoading={isLoadingPemasok}
                    />
                    <ControlledReactSelect
                      control={control}
                      name="supplierRegistrasiId"
                      label="Surat Registrasi B3 Pemasok"
                      options={dataSKPemasok?.registrasi?.map((item) => ({ value: item.id, label: labelSK(item) }))}
                      isLoading={isLoadingSKPemasok}
                      isDisabled={!supplierCompanyId}
                    />
                  </>
                )}

                {/* Isinya mengikuti bahan B3 pada SK yang dipilih di atas. */}
                <ControlledReactSelect
                  isDisabled={!skTerpilih}
                  control={control}
                  name="dataBahanB3Id"
                  label="Bahan B3"
                  options={opsiBahanB3}
                  isLoading={isLoadingMaterial || isLoadingRegistration || isLoadingSKPemasok}
                />
              </ModalBody>
              <ModalFooter>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} color="danger" variant="light" onPress={onClose}>
                  Tutup
                </Button>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                  Simpan
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
